import { useCountdown, padNumber } from '@/hooks/use-countdown'
import { cn } from '@/lib/utils'

interface CountdownTimerProps {
  targetDate: Date | string | null
  onExpire?: () => void
  className?: string
  size?: 'sm' | 'md' | 'lg'
  variant?: 'default' | 'light' | 'dark' | 'compact' | 'hero'
}

export function CountdownTimer({
  targetDate,
  onExpire,
  className,
  size = 'md',
  variant = 'default',
}: CountdownTimerProps) {
  const { timeLeft, isExpired } = useCountdown(targetDate, { onExpire })

  if (isExpired || !targetDate) {
    return (
      <div className={cn('text-center text-zinc-500', className)}>
        Deal has ended
      </div>
    )
  }

  const timeUnits = [
    { value: timeLeft.days, label: 'Days', shortLabel: 'D' },
    { value: timeLeft.hours, label: 'Hours', shortLabel: 'H' },
    { value: timeLeft.minutes, label: 'Mins', shortLabel: 'M' },
    { value: timeLeft.seconds, label: 'Secs', shortLabel: 'S' },
  ]

  // Compact variant - inline with colons
  if (variant === 'compact') {
    return (
      <div className={cn('flex items-center gap-1 font-mono text-zinc-900', className)}>
        {timeUnits.map((unit, i) => (
          <span key={unit.label} className="flex items-center">
            <span className="tabular-nums">{padNumber(unit.value)}</span>
            <span className="text-zinc-500">{unit.shortLabel}</span>
            {i < timeUnits.length - 1 && <span className="mx-1 text-zinc-400">:</span>}
          </span>
        ))}
      </div>
    )
  }

  // Size classes
  const sizeClasses = {
    sm: {
      container: 'gap-2',
      segment: 'px-2 py-1.5 min-w-10',
      value: 'text-lg',
      label: 'text-[9px]',
      separator: 'text-lg',
    },
    md: {
      container: 'gap-3',
      segment: 'px-3 py-2 min-w-14',
      value: 'text-2xl',
      label: 'text-[10px]',
      separator: 'text-2xl',
    },
    lg: {
      container: 'gap-4',
      segment: 'px-4 py-3 min-w-16',
      value: 'text-3xl md:text-4xl',
      label: 'text-xs',
      separator: 'text-3xl',
    },
  }

  const styles = sizeClasses[size]

  // Variant styling
  const getSegmentClasses = () => {
    switch (variant) {
      case 'hero':
        return 'rounded-lg bg-white/5 backdrop-blur-sm border border-white/10'
      case 'dark':
        return 'rounded-lg bg-zinc-900 border border-zinc-800'
      case 'light':
        return 'rounded-lg bg-zinc-100 border border-zinc-200'
      default:
        return 'rounded-lg bg-card border border-border'
    }
  }

  const getValueClasses = () => {
    switch (variant) {
      case 'hero':
        return 'text-white'
      case 'dark':
        return 'text-white'
      case 'light':
        return 'text-zinc-900'
      default:
        return 'text-foreground'
    }
  }

  const getLabelClasses = () => {
    switch (variant) {
      case 'hero':
        return 'text-white/50'
      case 'dark':
        return 'text-zinc-500'
      case 'light':
        return 'text-zinc-500'
      default:
        return 'text-muted-foreground'
    }
  }

  const getSeparatorClasses = () => {
    switch (variant) {
      case 'hero':
        return 'text-white/20'
      case 'dark':
        return 'text-red-500/50'
      case 'light':
        return 'text-red-400/50'
      default:
        return 'text-muted-foreground/30'
    }
  }

  return (
    <div className={cn('flex items-center justify-center', styles.container, className)}>
      {timeUnits.map((unit, index) => (
        <div key={unit.label} className="flex items-center">
          {/* Time segment */}
          <div className={cn('flex flex-col items-center', getSegmentClasses(), styles.segment)}>
            <span className={cn(
              'font-bold tabular-nums leading-none',
              styles.value,
              getValueClasses()
            )}>
              {padNumber(unit.value)}
            </span>
            <span className={cn(
              'mt-1 uppercase tracking-wider',
              styles.label,
              getLabelClasses()
            )}>
              {unit.label}
            </span>
          </div>

          {/* Separator (except after last) */}
          {index < timeUnits.length - 1 && (
            <span className={cn(
              'mx-1 font-bold',
              styles.separator,
              getSeparatorClasses()
            )}>
              :
            </span>
          )}
        </div>
      ))}
    </div>
  )
}

/**
 * Floating countdown badge for headers or sticky bars
 */
export function CountdownBadge({
  targetDate,
  label = 'Ends in',
  className,
}: {
  targetDate: Date | string | null
  label?: string
  className?: string
}) {
  const { timeLeft, isExpired } = useCountdown(targetDate)

  if (isExpired || !targetDate) {
    return null
  }

  const formatTime = () => {
    if (timeLeft.days > 0) {
      return `${timeLeft.days}d ${timeLeft.hours}h`
    }
    if (timeLeft.hours > 0) {
      return `${timeLeft.hours}h ${timeLeft.minutes}m`
    }
    return `${timeLeft.minutes}m ${timeLeft.seconds}s`
  }

  return (
    <div className={cn(
      'inline-flex items-center gap-2 rounded-full bg-red-50 border border-red-100 px-3 py-1 text-sm',
      className
    )}>
      <span className="text-zinc-500">{label}</span>
      <span className="font-mono font-bold text-red-600">{formatTime()}</span>
    </div>
  )
}
